/* ──────────────────────────────────────────────────────────────
 *  useTrainingData — fetch training data from backend API
 *
 *  Fetches training samples for the current bushing type via the
 *  backend API endpoint and populates useSampleStore.originalSamples
 *  so scatter plots display training points immediately.
 * ────────────────────────────────────────────────────────────── */

import { useEffect, useRef } from 'react';
import { useBushingStore } from '@/stores/useBushingStore';
import { useSampleStore } from '@/stores/useSampleStore';
import { fetchTrainingData } from '@/api/trainingDataApi';

/**
 * Hook — fetches training data for the current bushing type from the
 * backend API on mount and whenever the bushing type changes.
 *
 * On type change, generated samples and selection state are cleared
 * before the fetch so stale data from the previous type is never
 * rendered through the new type's lens.
 */
export function useTrainingData(): void {
  const bushingType = useBushingStore((s) => s.bushingType);
  const setOriginalSamples = useSampleStore((s) => s.setOriginalSamples);
  const clearGeneratedSamples = useSampleStore((s) => s.clearGeneratedSamples);

  // Use a ref to avoid duplicate fetches during React strict-mode double-mount
  const lastFetchedType = useRef<string | null>(null);

  useEffect(() => {
    if (lastFetchedType.current === bushingType) return;
    lastFetchedType.current = bushingType;

    // Clear stale generated samples and selection before the new type's data
    // arrives — prevents components from reading A1 samples through a B1 lens.
    clearGeneratedSamples();

    fetchTrainingData(bushingType)
      .then((response) => {
        setOriginalSamples(response.data);
      })
      .catch((err) => {
        console.error('[useTrainingData] Failed to load training data:', err);
      });
  }, [bushingType, setOriginalSamples, clearGeneratedSamples]);
}
